import * as XLSX from 'xlsx-js-style';
import { stripHtml } from '../../utils/sanitize';
import { workbookToXlsxBlob } from '../../utils/workbookToXlsxBlob';
import { applyYellowHeaderRowAndAutofilter, setSheetColumnWidths } from './kpiDashboardExcelExport';
import type { MsrRawTicketRow } from './msrReportTypes';

/** Column layout aligned with open backlog / drill-down ticket exports, plus MSR chart fields. */
const RAW_COLUMNS: Array<{ key: keyof MsrRawTicketRow; header: string }> = [
  { key: 'ticketNumber', header: 'Number' },
  { key: 'shortDescription', header: 'Short Description' },
  { key: 'category', header: 'Type' },
  { key: 'priority', header: 'Priority' },
  { key: 'state', header: 'State' },
  { key: 'team', header: 'Assignment Group' },
  { key: 'assignedTo', header: 'Assigned To' },
  { key: 'company', header: 'Company' },
  { key: 'resolutionTime', header: 'Resolution Time' },
  { key: 'ci', header: 'CI' },
  { key: 'otherResourceNames', header: 'Other Resource Names' },
  { key: 'slaStatus', header: 'Ticket SLA Status' },
  { key: 'createdAt', header: 'Ticket Created' },
  { key: 'resolvedAt', header: 'Ticket Resolved' },
  { key: 'lastUpdatedTime', header: 'Last Updated Time' },
  { key: 'lastUpdatedBy', header: 'Last Updated By' },
  { key: 'channel', header: 'Channel' },
  { key: 'remarks', header: 'Remarks' },
];

export type MsrRawCsvExport = { blob: Blob; fileName: string };
export type MsrRawExcelExport = { blob: Blob; fileName: string };

function cellValue(key: keyof MsrRawTicketRow, value: string | null | undefined): string {
  if (value == null) return '';
  if (key === 'shortDescription' || key === 'remarks') {
    return stripHtml(value);
  }
  return String(value);
}

function msrRawFilename(customerName: string, dateFrom: string, dateTo: string, ext: 'csv' | 'xlsx'): string {
  const safe = customerName.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || 'customer';
  const from = dateFrom.slice(0, 10) || 'unknown';
  const to = dateTo.slice(0, 10) || 'unknown';
  return `MSR_Raw_Data_${safe}_from_${from}_to_${to}.${ext}`;
}

function buildMsrRawRows(rows: MsrRawTicketRow[]): string[][] {
  return rows.map((row) => RAW_COLUMNS.map((col) => cellValue(col.key, row[col.key])));
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('\n') || value.includes('"') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildMsrRawCsv(rows: MsrRawTicketRow[]): string {
  const lines: string[] = [
    RAW_COLUMNS.map((col) => escapeCsvField(col.header)).join(','),
    ...buildMsrRawRows(rows).map((row) => row.map((cell) => escapeCsvField(cell)).join(',')),
  ];
  return lines.join('\n');
}

export function exportMsrRawDataCsv(
  customerName: string,
  dateFrom: string,
  dateTo: string,
  rows: MsrRawTicketRow[]
): MsrRawCsvExport {
  const csv = buildMsrRawCsv(rows);
  return {
    blob: new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    fileName: msrRawFilename(customerName, dateFrom, dateTo, 'csv'),
  };
}

export function exportMsrRawDataExcel(
  customerName: string,
  dateFrom: string,
  dateTo: string,
  rows: MsrRawTicketRow[]
): MsrRawExcelExport {
  const headers = RAW_COLUMNS.map((col) => col.header);
  const dataRows = buildMsrRawRows(rows);
  const aoa: string[][] = [headers, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(
    ws,
    headers.map((_, i) => (i === 1 ? 48 : i >= 12 ? 18 : 14))
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Raw Data');
  return {
    blob: workbookToXlsxBlob(wb),
    fileName: msrRawFilename(customerName, dateFrom, dateTo, 'xlsx'),
  };
}
